import BroadcastEventManager from "@/js/Shared/Events/Managers/BroadcastEventManager";
import IdentitiesController  from "../Settings/Identities/IdentitiesController";
import FoldersController     from "../Mailboxes/FoldersController";
import ContactsController    from "../Contacts/ContactsController";
import StateConsumer         from "../Shared/State/StateConsumer";

export { PopupControllerSingleton as default };

/**
* Popup controller
*
* Performas actions in popups
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class PopupController {
  /**
   * Defins defaut popup params
   *
   * @param {Object}
   *
   * @private
   *
   * @since 7.0.0
   */
  defaults = {
    width      : 800,
    height     : 650,
    left       : 0,
    top        : 0,
    menubar    : 'no',
    location   : 'no',
    scrollbars : 'no',
    resizable  : 'no',
    status     : 'no',
  };

  /**
   * Popup path
   *
   * @param {String}
   *
   * @private
   *
   * @since 7.0.0
   */
  popup_path = window.location.origin + window.location.pathname;

  /**
   * Holds open popups
   *
   * @param {Object}
   *
   * @private
   *
   * @since 7.0.0
   */
  windows = {};

  /**
   * Event Broadcaster
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {BroadcastEventManager}
   */
  broadcaster = new BroadcastEventManager();

  /**
   * Opens a popup window
   *
   * Returns popup id
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  open(params = {}) {
    params.path = this.popup_path + '#/p' + params.path;
    let id      = this.setWindow(params);
    return id;
  }

  openExternal(params = {}) {
    let id      = this.setWindow(params);
    return id;
  }

  /**
   * Returns popup data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Object|Array|null}
   */
  getPopupData() {
    return (window.opener.popup_data) || null;
  }

  /**
   * Checks if window is a created popup
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  isValidPopup() {
    try {
      if (window.opener && window.opener.valid_popup) {
        return true;
      }
    } catch (e) {
      return false;
    }
  }

  /**
   * Closes all popups
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  closeAllPopups() {
    for (let id in this.windows) {
      this.closePopup(id);
    }
  }

  /**
   * Closes a popup by id
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} id popup id
   *
   * @return {void}
   */
  closePopup(id) {
    this.windows[id].close();
  }

  /**
   * Closes the popup if the window is in a popup
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  closeCurrentPopup() {
    if (window.opener && window.opener.popup) {
      window.close();
    }
  }

  /**
   * Sets a new window, opens a popup
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} params popup params
   *
   * @return {String}
   */
  setWindow(params) {
    let id    = Math.random().toString(18) + Date.now();
    let popup = ''
              + 'width='       +  (params.width      || this.defaults.width)
              + ',height='     +  (params.height     || this.defaults.height)
              + ',left='       +  (params.left       || this.defaults.left)
              + ',top='        +  (params.top        || this.defaults.top)
              + ',menubar='    +  (params.menubar    || this.defaults.menubar)
              + ',location='   +  (params.location   || this.defaults.location)
              + ',scrollbars=' +  (params.scrollbars || this.defaults.scrollbars)
              + ',resizable='  +  (params.resizable  || this.defaults.resizable)
              + ',status='     +  (params.status     || this.defaults.status)

    let open = window.open(params.path, id, popup);

    if (!open) {
      return;
    }

    try {
      this.windows[id]                   = open;
      this.windows[id].opener            = window.opener || {};

      this.windows[id].opener.popup_data  = params.data || {};
      this.windows[id].opener.valid_popup = true;
      this.windows[id].opener.popup       = this.windows[id];

      this.windows[id].opener.popup_data.identities_controller = IdentitiesController;
      this.windows[id].opener.popup_data.folders_controller    = FoldersController;
      this.windows[id].opener.popup_data.contacts_controller   = ContactsController;
      this.windows[id].opener.popup_data.state_consumer        = StateConsumer;

      let controller = this;

      this.windows[id].onload = function() {
          this.onbeforeunload = () => {
            controller.broadcaster.broadcast('popup-closed');
            controller.cleanUpWindow(id);
          }
      }
    } catch (e) {
      console.error('Error opening popup:', e);
      return null;
    }

    return id;
  }

  /**
   * Cleans up popup data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} id     popup id
   *
   * @return {void}
   */
  cleanUpWindow(id) {
    delete this.windows[id];
  }
}

const PopupControllerSingleton = new PopupController();
